/**
 * Project Phasing — phased financial integration (PP pack PP8).
 *
 * Adds per-phase LCOE computation and a capacity-weighted blended LCOE
 * that closes back to the single-phase LCOE when only one phase is
 * present.
 *
 * DIMENSIONAL ANALYSIS (capacity-weighted blended LCOE):
 *
 *   Per-phase energy over remaining project life:
 *     energy_i  = capacity_i [MW]
 *               × CF [fraction]
 *               × hours_per_year [hr/yr]
 *               × life_i [yr]
 *               = MWh
 *
 *   Per-phase cost = LCOE_i [USD/MWh] × energy_i [MWh] = USD
 *
 *   Blended LCOE = Σ cost_i / Σ energy_i
 *                = (USD) / (MWh)
 *                = USD/MWh    ✓
 *
 * When the capacity factor + project life are identical across phases
 * (the typical case for FA-level inputs), CF and hours_per_year cancel
 * out and reduce to:
 *
 *   weight_i = capacity_i [MW] × life_i [yr]    units: MW·yr
 *   blended  = Σ (LCOE_i × weight_i) / Σ weight_i
 *
 * Single-phase regression: weight_1 = mw × life,
 *                          blended = LCOE_1 × weight_1 / weight_1 = LCOE_1.
 * Verified by `computeBlendedLcoe.singlePhaseEqualsBaseline` test.
 *
 * IRR / NPV on phased cashflows:
 *   Each phase's CAPEX is an outflow at year (yearsUntilPhaseCOD).
 *   Each phase's revenue starts at its COD and runs through project end.
 *   These are SUMMED across phases per year, then a single IRR is solved
 *   on the aggregate cashflow vector — NOT summed per-phase IRRs. Per-
 *   phase IRRs are returned alongside for transparency.
 */
import type { ProjectIntent, ProjectPhase } from '../types/siteRag';
import type { FinancialAssumptionsCore } from '../types/financialAssumptions';
import type { BomItem } from '../types/supplyChain';
import { effectivePhases, phaseIncrementMW } from './projectIntent';
import {
  HOURS_PER_YEAR,
  KW_PER_MW,
  buildCapexDrawdownByYear,
  capitalRecoveryFactor,
  computeAnnualMwh,
  computeAnnualOpexUsd,
  computeAnnualFuelUsd,
  computeIdcUsd,
  computeLcoeUsdPerMwh,
  computeLcoeRealUsdPerMwh,
  computePtcCreditUsdForYear,
  computeYearOneRevenueUsd,
  discountedSum,
  revenuePriceForYear,
  solveIrrFraction,
  opexLevelisationFactor,
} from './projectFinance';
import { resolveWcDecommUsd } from '../config/financeFlags';
import {
  DEFAULT_INFLATION_PCT_PER_YEAR,
  fuelEscalatorPctPerYear,
  realDiscountRateFraction,
  realEscalatorFraction,
} from './inflationBasis';

/**
 * A1 + A2 — per-phase CAPEX SOURCE (Wave A keystone).
 *
 * Lets the caller hand the phased engine the AUTHORITATIVE per-phase CAPEX
 * (BoM-derived, from `computePerPhaseCapexFromBom`) instead of forcing it to
 * re-derive every phase's CAPEX as a capacity-fraction split of the project
 * total. Keyed by `phaseId` (=== `ProjectPhase.id`).
 *
 *   - `capexTotalUsd`: when finite & > 0, this phase's CAPEX is AUTHORITATIVE —
 *     the engine uses it directly (A1 override). When absent / 0 / non-finite,
 *     the engine falls back to the legacy capacity-fraction split for that phase
 *     only, so unpriced phases still get a sensible allocation.
 *   - `bom`: the BoM lines attributed to this phase. When present AND any line
 *     carries `costTiming`, the engine spreads this phase's CAPEX across years
 *     via the drawdown curve (A2) instead of lumping it in the COD year.
 *
 * Absent map entry ⇒ pure legacy behaviour for that phase (capacity-fraction
 * split, whole-cost at COD). Absent map entirely ⇒ today's behaviour for every
 * phase — back-compat for all current callers.
 */
export interface PhaseCapexSource {
  capexTotalUsd?: number | null;
  bom?: readonly BomItem[] | null;
  /**
   * R-58 — PER-PHASE FINANCIAL OVERRIDES from `FinancialAssumptions.phases[]`,
   * linked to this intent phase by id.
   *
   * The schema has always documented that "when `modelVersion >= 2`, formulas
   * prefer this array", and the form has always let the user set a capacity
   * factor / COD / O&M per phase — but the engine read NONE of it: every phase
   * was priced at the CAMPUS capacity factor and given a capacity-fraction
   * slice of campus OPEX, so editing a phase's CF or COD moved no number in the
   * report. These fields close that gap.
   *
   * Every field is OPTIONAL and falls back to the campus value INDIVIDUALLY —
   * a phase that overrides only its capacity factor still inherits campus O&M.
   */
  capacityFactorPct?: number | null;
  fixedOmPerKwYearUsd?: number | null;
  variableOmPerMwhUsd?: number | null;
  fuelCostPerMwhUsd?: number | null;
  /**
   * Overrides `ProjectPhase.targetInServiceDate` when the FA phase carries its
   * own COD. Named `codIsoOverride` rather than `inServiceDate` so it cannot be
   * mistaken for — or tripped up by the SSOT sentinel guarding — the retired
   * `inServiceDate?:` field on the per-report `*Preferences` interfaces. SSOT-SENTINEL-OK
   */
  codIsoOverride?: string | null;
}
export type PhaseCapexSourceMap = Record<string, PhaseCapexSource>;

/** First finite number in the list, else null. Used for per-phase → campus fallback. */
function firstFinite(...values: Array<number | null | undefined>): number | null {
  for (const v of values) {
    if (v != null && Number.isFinite(v)) return v;
  }
  return null;
}

/**
 * Resolve a phase's CAPEX: AUTHORITATIVE BoM-derived value when present
 * (A1), else the capacity-fraction fallback. Returns null when neither is
 * available (e.g. no project CAPEX and no BoM rollup).
 */
function resolvePhaseCapexUsd(
  source: PhaseCapexSource | undefined,
  projectCapex: number | null,
  capacityFraction: number,
): number | null {
  const authoritative = source?.capexTotalUsd;
  if (authoritative != null && Number.isFinite(authoritative) && authoritative > 0) {
    return authoritative;
  }
  // Fallback: capacity-fraction split of the project total (legacy path,
  // retained ONLY for phases with no priced BoM lines).
  return projectCapex != null ? projectCapex * capacityFraction : null;
}

/**
 * A phase's EFFECTIVE capital: its own CAPEX, risked and financed the same way
 * `computeEffectiveCapexUsd` treats the campus figure.
 *
 *   (phase capex + land, if this is the first phase) × (1 + contingency) + IDC
 *
 * CONTINGENCY and IDC are RATES, so they apply per phase. LAND is bought once,
 * up front, so it attaches to the EARLIEST phase rather than being spread — a
 * later phase does not re-buy the site.
 *
 * Without this the phased and single-phase engines disagree by exactly the
 * contingency + IDC uplift, and the closure that anchors the phased blend to
 * `computeLcoeUsdPerMwh` breaks.
 */
function resolveEffectivePhaseCapexUsd(
  phaseCapexUsd: number | null,
  financial: FinancialAssumptionsCore,
  isFirstPhase: boolean,
): number | null {
  if (phaseCapexUsd == null || !Number.isFinite(phaseCapexUsd)) return phaseCapexUsd;
  const land = isFirstPhase ? financial.landCost?.totalUsd : null;
  const withLand = phaseCapexUsd + (land != null && Number.isFinite(land) && land > 0 ? land : 0);
  const contingencyPct = financial.contingencyPct;
  const risked = contingencyPct != null && Number.isFinite(contingencyPct) && contingencyPct > 0
    ? withLand * (1 + contingencyPct / 100)
    : withLand;
  return risked + computeIdcUsd(financial, risked);
}

/** The capacity factor this phase generates at — its own, else the campus rate. */
function resolvePhaseCapacityFactorPct(
  source: PhaseCapexSource | undefined,
  financial: FinancialAssumptionsCore,
): number | null {
  return firstFinite(source?.capacityFactorPct, financial.capacityFactorPct);
}

/**
 * Annual OPEX for ONE phase, in USD/yr, computed from the phase's own O&M
 * RATES against the plant that phase actually adds:
 *
 *   opex_i = fixedOm [$/kW/yr] × increment_i [MW] × 1000 [kW/MW]
 *          + (variableOm + fuel) [$/MWh] × annualMwh_i [MWh/yr]
 *
 * WHY NOT the old `campusOpex × capacityFraction`: campus OPEX is computed
 * against `financial.capacityMW` (the ULTIMATE campus nameplate) while the
 * fractions are taken over the INTENT phase increments. When the FA capacity and
 * the intent capacity disagree — routine, since they are separately entered and
 * only cross-VALIDATED with a warning — the split silently priced a phase's
 * operating cost off a different plant than its revenue. Deriving from the rates
 * removes the mismatch entirely and lets a phase carry its own O&M.
 *
 * Returns null when neither the phase nor the campus supplies a fixed-O&M rate
 * (the same condition under which `computeAnnualOpexUsd` returns null).
 */
function resolvePhaseOpexUsd(
  source: PhaseCapexSource | undefined,
  financial: FinancialAssumptionsCore,
  incrementMw: number,
  annualMwh: number,
  /** This phase's EFFECTIVE capital — the base for insurance & property tax. */
  phaseEffectiveCapexUsd: number | null,
): number | null {
  const fixedOmPerKwYear = firstFinite(source?.fixedOmPerKwYearUsd, financial.fixedOmPerKwYearUsd);
  if (fixedOmPerKwYear == null) return null;
  const variableOmPerMwh = firstFinite(source?.variableOmPerMwhUsd, financial.variableOmPerMwhUsd) ?? 0;
  const fuelPerMwh = firstFinite(source?.fuelCostPerMwhUsd, financial.fuelCostPerMwhUsd) ?? 0;
  // Insurance + property tax is an annual PERCENT OF CAPITAL, so each phase
  // carries it against its OWN capital. Omitting it here was the single reason
  // the phased and single-phase engines disagreed once the campus formula
  // started charging it — a constant per-year gap equal to the whole insurance
  // line.
  const insurancePct = financial.insuranceAndPropertyTaxPct;
  const insurance = insurancePct != null && Number.isFinite(insurancePct) && insurancePct > 0
    && phaseEffectiveCapexUsd != null && phaseEffectiveCapexUsd > 0
    ? phaseEffectiveCapexUsd * (insurancePct / 100)
    : 0;
  return fixedOmPerKwYear * incrementMw * KW_PER_MW
    + (variableOmPerMwh + fuelPerMwh) * annualMwh
    + insurance;
}

export interface PerPhaseFinancials {
  phaseId: string;
  sequence: number;
  /**
   * Phase capacity in MW. Under the cumulative convention this is the
   * INCREMENT added by this phase (the energy/CAPEX basis), i.e.
   * `phaseIncrementMW(phases, i)` — NOT the cumulative-at-phase figure.
   * Kept named `capacityMW` for back-compat; prefer `incrementMW` /
   * `cumulativeMW` below when the basis matters for display.
   */
  capacityMW: number | null;
  /** MW ADDED this phase (energy/CAPEX basis). Null when TBD. */
  incrementMW?: number | null;
  /** Cumulative site MW AT this phase's completion (load basis). Null when TBD. */
  cumulativeMW?: number | null;
  /** CAPEX attributed to this phase in USD. Null when TBD. */
  capexUsd: number | null;
  /** OPEX per year attributed to this phase in USD/yr. Null when TBD. */
  opexAnnualUsd: number | null;
  /** Phase COD ISO date. */
  codDate: string | null;
  /**
   * Per-phase LCOE in USD/MWh. Computed from this phase's CAPEX + OPEX
   * against the project-life-remaining horizon. Null when inputs are
   * incomplete.
   */
  lcoeUsdPerMwh: number | null;
  /** The same phase in constant dollars — see BlendedLcoeResult. */
  lcoeRealUsdPerMwh?: number | null;
}

export interface BlendedLcoeResult {
  perPhase: PerPhaseFinancials[];
  /** Capacity-weighted blended LCOE in USD/MWh — NOMINAL. */
  blendedLcoeUsdPerMwh: number | null;
  /**
   * The same blend in CONSTANT (real) dollars.
   *
   * Carried so a phased project's two headline figures stay on the SAME basis.
   * Without it the report stamp paired a BLENDED nominal LCOE with a
   * SINGLE-PHASE real one — two numbers describing different things under
   * labels that implied one project.
   */
  blendedLcoeRealUsdPerMwh: number | null;
  /** Sum of per-phase weights (MW·yr) — surfaces basis for the blend. */
  totalWeight: number;
  /** Human-readable basis description. */
  blendBasis: string;
}

/**
 * Compute per-phase LCOE and the capacity-weighted blended LCOE.
 *
 * When `intent.phases` is absent / empty, returns a one-element result
 * with the single-phase LCOE — this is the back-compat closure that
 * makes downstream LCOE consumers branch-free.
 */
export function computeBlendedLcoe(
  intent: ProjectIntent | undefined | null,
  financial: FinancialAssumptionsCore,
  phaseCapexSource?: PhaseCapexSourceMap,
): BlendedLcoeResult {
  const view = effectivePhases(intent);
  const phases = view.phases;

  // Single-phase / synthetic path — exact reproduction of the existing
  // single-phase LCOE. Pin: single-phase blend MUST equal
  // computeLcoeUsdPerMwh(financial) within numerical tolerance.
  if (!view.isPhased) {
    const baseline = computeLcoeUsdPerMwh(financial);
    const baselineReal = computeLcoeRealUsdPerMwh(financial);
    return {
      perPhase: [{
        phaseId: phases[0].id,
        sequence: phases[0].sequence,
        capacityMW: financial.capacityMW,
        capexUsd: financial.capexTotalUsd,
        opexAnnualUsd: computeAnnualOpexUsd(financial),
        codDate: financial.inServiceDate ?? null,
        lcoeUsdPerMwh: baseline,
      }],
      blendedLcoeUsdPerMwh: baseline,
      blendedLcoeRealUsdPerMwh: baselineReal,
      totalWeight: financial.capacityMW != null && financial.projectLifeYears != null
        ? financial.capacityMW * financial.projectLifeYears
        : 0,
      blendBasis: 'Single-phase project — blended LCOE equals project LCOE.',
    };
  }

  // Phased — compute per-phase LCOE.
  // A1: per-phase CAPEX is the AUTHORITATIVE BoM-derived value when the caller
  // supplies one via `phaseCapexSource` (populated by
  // computePerPhaseCapexFromBom); only phases with NO priced BoM lines fall
  // back to the capacity-fraction split of the project total.
  const totalCapacityMW = view.totalCapacityMW ?? 0;
  const projectCapex = financial.capexTotalUsd ?? null;
  const projectLife = financial.projectLifeYears ?? null;
  // RESOLVED per-phase CODs (FA phase override → intent target). Computed here
  // so the LCOE horizon and the retirement anchor use the same dates the
  // cashflow schedule does — an FA COD override used to move NPV/IRR but leave
  // the LCOE on the intent date.
  const phaseCodIsoById = new Map<string, string | null>(
    phases.map((ph) => [ph.id, phaseCapexSource?.[ph.id]?.codIsoOverride ?? ph.targetInServiceDate ?? null]),
  );
  const resolvedCods = [...phaseCodIsoById.values()].filter((d): d is string => Boolean(d)).sort();
  const ultimateCOD = resolvedCods.at(-1) ?? view.ultimateInServiceDate ?? financial.inServiceDate ?? null;
  const ultimateCODYear = ultimateCOD ? parseInt(ultimateCOD.slice(0, 4), 10) : null;

  const perPhase: PerPhaseFinancials[] = phases.map((phase, i) => {
    // Cumulative convention: the energy/CAPEX basis for a phase is the MW
    // ADDED that phase (the increment), NOT the cumulative-at-phase figure.
    // `phaseMw` below is the INCREMENT throughout this block.
    const cumulativeMw = phase.capacityMW;
    const phaseMw = phaseIncrementMW(phases, i);
    if (phaseMw == null || phaseMw <= 0) {
      return {
        phaseId: phase.id, sequence: phase.sequence, capacityMW: phaseMw,
        incrementMW: phaseMw, cumulativeMW: cumulativeMw,
        capexUsd: null, opexAnnualUsd: null, codDate: phaseCodIsoById.get(phase.id) ?? phase.targetInServiceDate,
        lcoeUsdPerMwh: null,
      };
    }
    // CAPEX allocation (A1): use the AUTHORITATIVE BoM-derived per-phase CAPEX
    // when the caller supplied one (`phaseCapex[phase.id].capexTotalUsd > 0`);
    // otherwise fall back to the legacy INCREMENT-fraction × total split. The
    // fallback denominator is the project total (= final cumulative = Σ
    // increments), so for an all-fallback project the fractions sum to 1.0 and
    // the LCOE rendering still closes back to the single-phase baseline.
    const capacityFraction = totalCapacityMW > 0 ? phaseMw / totalCapacityMW : 0;
    const phaseSource = phaseCapexSource?.[phase.id];
    const phaseCapex = resolveEffectivePhaseCapexUsd(
      resolvePhaseCapexUsd(phaseSource, projectCapex, capacityFraction),
      financial,
      i === 0,
    );

    // Effective phase life: how many years the phase generates. We
    // anchor every phase's end to the ultimate COD-year + projectLife
    // (i.e. all phases retire together). Phase life = projectLife -
    // yearsFromPhaseCODtoUltimate. Single-phase reduces to full life.
    const phaseLifeYr = derivePhaseLifeYears(phaseCodIsoById.get(phase.id), ultimateCODYear, projectLife);
    if (phaseLifeYr == null || phaseLifeYr <= 0) {
      return {
        phaseId: phase.id, sequence: phase.sequence, capacityMW: phaseMw,
        incrementMW: phaseMw, cumulativeMW: cumulativeMw,
        capexUsd: phaseCapex, opexAnnualUsd: null,
        codDate: phaseCodIsoById.get(phase.id) ?? phase.targetInServiceDate, lcoeUsdPerMwh: null,
      };
    }

    // Per-phase LCOE — same formula as computeLcoeUsdPerMwh, but with
    // per-phase capacity + capex + life. R-58: the capacity factor is the
    // PHASE's own when the FA supplies one (a gas bridge and an SMR phase do
    // not run at the same CF), else the campus rate.
    const cfPct = resolvePhaseCapacityFactorPct(phaseSource, financial);
    const wacc = financial.waccPct;
    if (phaseCapex == null || cfPct == null || wacc == null) {
      return {
        phaseId: phase.id, sequence: phase.sequence, capacityMW: phaseMw,
        incrementMW: phaseMw, cumulativeMW: cumulativeMw,
        capexUsd: phaseCapex, opexAnnualUsd: null,
        codDate: phaseCodIsoById.get(phase.id) ?? phase.targetInServiceDate, lcoeUsdPerMwh: null,
      };
    }
    // Energy basis = the NEW plant added this phase = the increment.
    const annualMwh = computeAnnualMwh(phaseMw, cfPct);
    if (annualMwh == null || annualMwh <= 0) {
      return {
        phaseId: phase.id, sequence: phase.sequence, capacityMW: phaseMw,
        incrementMW: phaseMw, cumulativeMW: cumulativeMw,
        capexUsd: phaseCapex, opexAnnualUsd: null,
        codDate: phaseCodIsoById.get(phase.id) ?? phase.targetInServiceDate, lcoeUsdPerMwh: null,
      };
    }
    // OPEX from the PHASE's own O&M rates against the plant it adds (R-58),
    // falling back per-field to the campus rates. Replaces the old
    // `campusOpex × capacityFraction` split — see `resolvePhaseOpexUsd`.
    const phaseOpex = resolvePhaseOpexUsd(phaseSource, financial, phaseMw, annualMwh, phaseCapex);
    if (phaseOpex == null) {
      return {
        phaseId: phase.id, sequence: phase.sequence, capacityMW: phaseMw,
        incrementMW: phaseMw, cumulativeMW: cumulativeMw,
        capexUsd: phaseCapex, opexAnnualUsd: null,
        codDate: phaseCodIsoById.get(phase.id) ?? phase.targetInServiceDate, lcoeUsdPerMwh: null,
      };
    }
    const crf = capitalRecoveryFactor(wacc / 100, phaseLifeYr);
    // B14 — levelise an ESCALATING opex, exactly as the single-phase engine
    // does. Without this the blended LCOE would drift from the legacy LCOE the
    // moment the flag came on, breaking the one-phase equivalence anchor.
    const phaseFuelShare = (() => {
      const total = computeAnnualOpexUsd(financial);
      const fuel = computeAnnualFuelUsd(financial);
      return total != null && fuel != null && total > 0 ? fuel / total : 0;
    })();
    const phaseLevelisedOpex =
      phaseOpex * (1 - phaseFuelShare) * opexLevelisationFactor(
        wacc / 100, DEFAULT_INFLATION_PCT_PER_YEAR / 100, phaseLifeYr)
      + phaseOpex * phaseFuelShare * opexLevelisationFactor(
        wacc / 100, fuelEscalatorPctPerYear(financial.technology) / 100, phaseLifeYr);
    const phaseLcoe = (phaseCapex * crf + phaseLevelisedOpex) / annualMwh;
    // REAL counterpart, on the same inputs: deflated rate, non-fuel flat, fuel
    // on its deflated commodity path. Same Fisher pairing as the single-phase
    // engine, so a one-phase project reconciles on BOTH bases.
    const realRate = realDiscountRateFraction(wacc / 100);
    const phaseLcoeReal = (
      phaseCapex * capitalRecoveryFactor(realRate, phaseLifeYr)
      + phaseOpex * (1 - phaseFuelShare) * opexLevelisationFactor(realRate, 0, phaseLifeYr)
      + phaseOpex * phaseFuelShare * opexLevelisationFactor(
          realRate,
          realEscalatorFraction(fuelEscalatorPctPerYear(financial.technology) / 100),
          phaseLifeYr)
    ) / annualMwh;
    return {
      phaseId: phase.id, sequence: phase.sequence, capacityMW: phaseMw,
      incrementMW: phaseMw, cumulativeMW: cumulativeMw,
      capexUsd: phaseCapex, opexAnnualUsd: phaseOpex,
      codDate: phaseCodIsoById.get(phase.id) ?? phase.targetInServiceDate, lcoeUsdPerMwh: phaseLcoe,
      lcoeRealUsdPerMwh: phaseLcoeReal,
    };
  });

  // Capacity-weighted blend. Weight = increment_i × phaseLife_i [MW·yr].
  // (`p.capacityMW` here is already the per-phase INCREMENT — the energy
  // contributed by that phase's NEW plant — so the blend reflects each
  // phase's added generation, not the double-counting cumulative figure.)
  let weightedSum = 0;
  let weightedRealSum = 0;
  let totalWeight = 0;
  for (const p of perPhase) {
    if (p.lcoeUsdPerMwh == null || p.capacityMW == null) continue;
    const life = derivePhaseLifeYears(
      phaseCodIsoById.get(p.phaseId),
      ultimateCODYear,
      projectLife,
    );
    if (life == null || life <= 0) continue;
    const w = p.capacityMW * life;
    weightedSum += p.lcoeUsdPerMwh * w;
    if (p.lcoeRealUsdPerMwh != null) weightedRealSum += p.lcoeRealUsdPerMwh * w;
    totalWeight += w;
  }
  const blended = totalWeight > 0 ? weightedSum / totalWeight : null;
  const blendedReal = totalWeight > 0 ? weightedRealSum / totalWeight : null;

  return {
    perPhase,
    blendedLcoeUsdPerMwh: blended,
    blendedLcoeRealUsdPerMwh: blendedReal,
    totalWeight,
    blendBasis: `Capacity × phase-life weighted (Σ MW·yr = ${totalWeight.toFixed(1)})`,
  };
}

/**
 * Phase life — how many years this phase is operating. All phases are
 * assumed to retire at `ultimateCOD + projectLife` (an MVP simplification;
 * fully-rigorous modelling would carry per-phase retirement dates).
 *
 * phaseLife = (ultimateCODYear + projectLife) - phaseCODYear
 *
 * When phase has no COD, falls back to full project life.
 */
function derivePhaseLifeYears(
  /** RESOLVED COD (FA phase override → intent target). Unused since each phase
   *  now carries its own design life; kept so callers read symmetrically. */
  _phaseCodIso: string | null | undefined,
  _ultimateCODYear: number | null,
  projectLifeYears: number | null,
): number | null {
  if (projectLifeYears == null) return null;
  // EACH PHASE OPERATES FOR ITS OWN DESIGN LIFE, measured from ITS OWN COD.
  //
  // This used to anchor every phase's retirement to `ultimateCOD +
  // projectLifeYears`, so an early phase in a staggered build operated for
  // LONGER than the design life it was given (a 25-year phase commissioned four
  // years before the last one ran for 29), inflating its energy and depressing
  // its LCOE. Equipment life runs from commissioning, not from the last phase's
  // schedule.
  const life = Math.round(projectLifeYears);
  return life >= 1 ? life : null;
}

/**
 * Phased CAPEX schedule — returns the year-by-year CAPEX outflow vector
 * for IRR / NPV. Index 0 = the EARLIEST outflow year. By default that is the
 * first phase COD year (each phase's CAPEX lumped at its own COD); A2
 * cost-timing can push it EARLIER (a deposit paid `orderLeadMonths` before the
 * first COD). A1: each phase's CAPEX is the authoritative BoM value when
 * `phaseCapexSource` supplies one, else a capacity-fraction split of the
 * project total.
 *
 * Returns null when any required input is missing — IRR/NPV computation
 * gracefully falls through to the legacy single-phase path.
 */
export interface PhasedCashflowResult {
  /** Year-by-year cashflow vector. Index 0 = first phase COD year. */
  flowsUsd: number[];
  /** ISO year labels matching flowsUsd indices. */
  yearLabels: number[];
  /** Per-phase per-year revenue (for transparency). */
  perPhaseAnnualRevenueUsd: Array<{ phaseId: string; revenueUsd: number | null }>;
  /**
   * Project IRR computed on the SUMMED phased flows (not Σ phase IRRs).
   * Returns null when convergence fails or cashflows are all one sign.
   */
  blendedIrrPct: number | null;
  /** Discounted NPV at financial.waccPct. */
  blendedNpvUsd: number | null;
  /**
   * The vector index the NPV is discounted TO — the project's first COD. Any
   * earlier index is pre-COD spend, compounded forward. Reported so a consumer
   * can state the valuation date rather than assume index 0.
   */
  npvBaseIndex: number;
}

/**
 * The ITC attributable to ONE phase — `itcPct` of that phase's own CAPEX.
 *
 * The credit follows the investment, so a phased build claims it phase by
 * phase as each one is placed in service, not once against the campus total.
 * Zero when no ITC is declared or the phase has no resolved CAPEX.
 */
function phaseItcUsd(financial: FinancialAssumptionsCore, phaseCapexUsd: number | null): number {
  const pct = financial.itcPct;
  if (pct == null || !Number.isFinite(pct) || pct <= 0) return 0;
  if (phaseCapexUsd == null || !Number.isFinite(phaseCapexUsd) || phaseCapexUsd <= 0) return 0;
  return phaseCapexUsd * (pct / 100);
}

export function computePhasedCashflows(
  intent: ProjectIntent | undefined | null,
  financial: FinancialAssumptionsCore,
  phaseCapexSource?: PhaseCapexSourceMap,
): PhasedCashflowResult | null {
  const view = effectivePhases(intent);
  if (financial.projectLifeYears == null) return null;

  const ultimateCODYear = view.ultimateInServiceDate
    ? parseInt(view.ultimateInServiceDate.slice(0, 4), 10)
    : (financial.inServiceDate ? parseInt(financial.inServiceDate.slice(0, 4), 10) : null);
  if (ultimateCODYear == null) return null;

  // Anchor: project's first COD year = earliest phase COD year (or COD).
  const firstCodYear = view.initialInServiceDate
    ? parseInt(view.initialInServiceDate.slice(0, 4), 10)
    : ultimateCODYear;

  const phases = view.phases;
  const totalCapacityMW = view.totalCapacityMW ?? 0;
  const projectCapex = financial.capexTotalUsd;

  // ── A1 + A2 pass 1 — resolve each phase's CAPEX (authoritative BoM value or
  // capacity-fraction fallback) and its year-by-year drawdown. We must do this
  // BEFORE sizing the vector because an A2 deposit (orderLeadMonths before COD)
  // can land in a year EARLIER than firstCodYear, widening the timeline. ──────
  interface PhaseCalc {
    phaseId: string;
    phaseMw: number;
    /** The CAPEX resolved for this phase (authoritative BoM value or split). */
    phaseCapexUsd: number | null;
    /** Energy at THIS phase's capacity factor (null when CF is unknown). */
    phaseAnnualMwh: number | null;
    phaseOpex: number | null;
    phaseCODYear: number;
    /** Year → CAPEX outflow (positive USD) for this phase. */
    drawdown: Map<number, number>;
  }
  const phaseCalcs: PhaseCalc[] = [];
  let earliestOutflowYear = firstCodYear;

  for (let i = 0; i < phases.length; i += 1) {
    const phase = phases[i];
    // Cumulative convention: OPEX/energy basis is the MW ADDED this phase
    // (increment). CAPEX is now A1-resolved (authoritative or fallback split).
    const phaseMw = phaseIncrementMW(phases, i);
    const source = phaseCapexSource?.[phase.id];
    // A phase that adds NO capacity can still cost money — a repower, a BESS
    // retrofit, a grid upgrade. Skipping it entirely (as this loop used to)
    // silently under-booked its CAPEX and overstated NPV/IRR, breaking the
    // engine's own "never creates or destroys CAPEX" invariant. Book the
    // capital; skip only the revenue, which genuinely needs capacity.
    const addsCapacity = phaseMw != null && phaseMw > 0;
    const capacityFraction = addsCapacity && totalCapacityMW > 0 ? phaseMw / totalCapacityMW : 0;
    const phaseCapex = resolveEffectivePhaseCapexUsd(
      resolvePhaseCapexUsd(source, projectCapex ?? null, capacityFraction),
      financial,
      i === 0,
    );
    // R-58 — the phase generates at ITS OWN capacity factor when the FA supplies
    // one, and its OPEX comes from its own O&M rates against the MW it adds.
    // Both used to be campus-wide, so a per-phase edit in the form moved no
    // number here (and the opex split was priced off the FA campus capacity
    // while the energy was priced off the intent increments).
    const phaseCfPct = resolvePhaseCapacityFactorPct(source, financial);
    // A capacity-less phase generates nothing and therefore earns nothing; its
    // CAPEX is still booked below.
    const phaseAnnualMwh = addsCapacity ? computeAnnualMwh(phaseMw!, phaseCfPct) : null;
    const phaseOpex = phaseAnnualMwh != null && addsCapacity
      ? resolvePhaseOpexUsd(source, financial, phaseMw!, phaseAnnualMwh, phaseCapex)
      : null;
    // COD: the FA phase's in-service date wins when it carries one, else the
    // intent phase's target, else the project's first COD.
    const phaseCodIso = source?.codIsoOverride ?? phase.targetInServiceDate;
    const phaseCODYear = phaseCodIso
      ? parseInt(phaseCodIso.slice(0, 4), 10)
      : firstCodYear;

    // A2 — drawdown curve. When the caller passed BoM lines WITH cost-timing for
    // this phase, spread the spend across years (deposit before COD, balance at
    // delivery/COD). Otherwise place the whole phase CAPEX in its COD year
    // (legacy behaviour, byte-identical for untimed phases).
    const bom = source?.bom ?? null;
    const hasTiming = bom != null && bom.some((it) => it.costTiming?.depositBasis != null);
    const drawdown = new Map<number, number>();
    if (hasTiming && bom != null && phaseCapex != null) {
      const lineDrawdown = buildCapexDrawdownByYear(bom, phaseCODYear);
      // CONSERVATION: cost-timing may only REDISTRIBUTE this phase's capital
      // across years — never change how much there is. The line total and the
      // authoritative phase figure are computed from different places (raw BoM
      // lines vs the A1 rollup, and the phase figure is additionally risked for
      // contingency + IDC), so the curve is SHAPED by the lines and SCALED to
      // the authoritative amount. Without this the drawdown silently replaced
      // the authoritative CAPEX with whatever the raw lines summed to.
      let lineTotal = 0;
      for (const usd of lineDrawdown.values()) lineTotal += usd;
      const scale = lineTotal > 0 ? phaseCapex / lineTotal : 0;
      for (const [yr, usd] of lineDrawdown.entries()) {
        drawdown.set(yr, (drawdown.get(yr) ?? 0) + usd * scale);
      }
      if (lineTotal <= 0) drawdown.set(phaseCODYear, phaseCapex);
    } else if (phaseCapex != null) {
      drawdown.set(phaseCODYear, (drawdown.get(phaseCODYear) ?? 0) + phaseCapex);
    }
    for (const yr of drawdown.keys()) earliestOutflowYear = Math.min(earliestOutflowYear, yr);

    phaseCalcs.push({ phaseId: phase.id, phaseMw: phaseMw ?? 0, phaseCapexUsd: phaseCapex, phaseAnnualMwh, phaseOpex, phaseCODYear, drawdown });
  }

  // ── UNIFIED CONVENTION (2026-08-19, decision Q9) ─────────────────────────
  // A unit's CAPEX lands in its COD year and it OPERATES for `projectLifeYears`
  // years AFTER that — index 0 is the investment, index 1 the first operating
  // year. This is the single-phase engine's convention, and adopting it here
  // ends a split that made the SAME project worth +21% NPV / +1pp IRR purely
  // by which engine ran (the phased path used to book revenue IN the COD year).
  //
  // Life is now measured from EACH phase's own COD rather than anchored to the
  // ultimate COD, so a phase operates for its design life instead of inheriting
  // a longer one from a later phase's schedule (an early phase previously ran
  // for 35 years on a 25-year design life).
  const lifeYears = Math.round(financial.projectLifeYears);
  if (!Number.isFinite(lifeYears) || lifeYears < 1) return null;
  const firstYear = earliestOutflowYear;
  const lastOperatingYear = Math.max(
    ...phaseCalcs.map((c) => c.phaseCODYear + lifeYears),
    ultimateCODYear + lifeYears,
  );
  const numYears = lastOperatingYear - firstYear + 1;
  if (numYears <= 0) return null;

  // WC lock-up / recovery + decommissioning, on the same basis as the
  // single-phase engine (flag OFF ⇒ {0,0} and nothing is added).
  const { workingCapitalUsd, decommissioningUsd } = resolveWcDecommUsd({
    workingCapitalUsd: financial.workingCapitalUsd,
    decommissioningUsd: financial.decommissioningUsd,
    yearOneRevenueUsd: computeYearOneRevenueUsd(financial),
    capexTotalUsd: financial.capexTotalUsd,
  });

  const flows = new Array<number>(numYears).fill(0);
  const yearLabels = Array.from({ length: numYears }, (_, i) => firstYear + i);
  const perPhaseRevenue: Array<{ phaseId: string; revenueUsd: number | null }> = [];

  const opexEscalator = DEFAULT_INFLATION_PCT_PER_YEAR / 100;
  // Fuel on its own commodity path — mirrors `projectFinance`, and must, or a
  // one-phase project stops reconciling between the two engines.
  const fuelEscalator = fuelEscalatorPctPerYear(financial.technology) / 100;
  const fuelShare = (() => {
    const total = computeAnnualOpexUsd(financial);
    const fuel = computeAnnualFuelUsd(financial);
    return total != null && fuel != null && total > 0 ? fuel / total : 0;
  })();
  // ── Pass 2 — book CAPEX drawdown (negative) + revenue/opex into the vector. ─
  const calcById = new Map(phaseCalcs.map((c) => [c.phaseId, c] as const));
  for (let i = 0; i < phases.length; i += 1) {
    const phase = phases[i];
    const calc = calcById.get(phase.id);
    if (!calc) {
      perPhaseRevenue.push({ phaseId: phase.id, revenueUsd: null });
      continue;
    }
    const { phaseAnnualMwh, phaseOpex, phaseCODYear, drawdown } = calc;

    // CAPEX outflows at their drawdown years.
    for (const [yr, usd] of drawdown.entries()) {
      const idx = yr - firstYear;
      if (idx >= 0 && idx < numYears) flows[idx] -= usd;
    }

    // Revenue from phase COD through project end. COD index is relative to the
    // (possibly pre-COD) vector start; firstYear ≤ firstCodYear ≤ phaseCODYear
    // so it is always ≥ 0, but clamp defensively.
    const codIdx = Math.max(0, phaseCODYear - firstYear);

    // Annual revenue from phase COD through project end.
    const ppaPrice = financial.ppaPricePerMwhUsd;
    // Energy at the PHASE's capacity factor (resolved in pass 1) — recomputing
    // it here at the campus CF is what made a per-phase CF edit inert.
    const annualMwh = phaseAnnualMwh;
    if (ppaPrice == null || annualMwh == null || phaseOpex == null) {
      perPhaseRevenue.push({ phaseId: phase.id, revenueUsd: null });
      continue;
    }
    const annualRevenue = ppaPrice * annualMwh;
    perPhaseRevenue.push({ phaseId: phase.id, revenueUsd: annualRevenue });
    // ITC lands with the capital, in the phase's COD year.
    const phaseItc = phaseItcUsd(financial, calc.phaseCapexUsd);
    if (phaseItc > 0 && codIdx >= 0 && codIdx < numYears) flows[codIdx] += phaseItc;
    // Operating years: t = 1 .. lifeYears, at index codIdx + t (COD+1).
    for (let t = 1; t <= lifeYears; t += 1) {
      const y = codIdx + t;
      if (y < 0 || y >= numYears) continue;
      // Contracted price through the PPA term, merchant after — the same
      // revenue model the single-phase engine uses, so the two agree.
      const price = revenuePriceForYear(financial, t);
      if (price == null) continue;
      // B14 — opex escalates on the SAME operating-year clock `t` as revenue.
      // Keying both to the phase's own COD is what preserves the one-phase
      // equivalence contract (a single-phase project must be byte-identical
      // through both engines, flag on AND off) and keeps revenue and cost on
      // one clock. Zero escalator short-circuits, so flag-off is unchanged.
      // Phase opex is a scaled slice of the campus figure, so the fuel/non-fuel
      // SHARE carries over even though the magnitude does not.
      const escalatedPhaseOpex =
        phaseOpex * (1 - fuelShare) * Math.pow(1 + opexEscalator, t - 1)
        + phaseOpex * fuelShare * Math.pow(1 + fuelEscalator, t - 1);
      flows[y] += price * annualMwh - escalatedPhaseOpex
        + computePtcCreditUsdForYear(financial, t, annualMwh);
    }
  }

  // Project-level working capital + decommissioning, booked once: locked up at
  // the first outflow, recovered (and decommissioning paid) in the final year.
  if (workingCapitalUsd > 0) {
    flows[0] -= workingCapitalUsd;
    flows[numYears - 1] += workingCapitalUsd;
  }
  if (decommissioningUsd > 0) flows[numYears - 1] -= decommissioningUsd;

  // ── IRR + NPV on the aggregate vector ────────────────────────────────────
  // BASE YEAR: the project's first COD, not index 0. The vector can start at a
  // pre-COD deposit year, so discounting to index 0 would silently re-base the
  // NPV whenever a scenario or a BoM edit moved the earliest outflow — two NPVs
  // for the same project would not be comparable. Pre-COD spend gets a negative
  // exponent and is compounded forward, which is the correct treatment.
  const baseIndex = Math.max(0, firstCodYear - firstYear);

  // ONE solver, shared with the single-phase engine. The local copy this
  // replaces had no bisection fallback, so a diverging Newton iteration
  // returned null and the caller silently reported the SINGLE-PHASE IRR under
  // the phased label.
  const irrFraction = solveIrrFraction(flows, baseIndex);
  const irr = irrFraction == null ? null : irrFraction * 100;

  const blendedNpvUsd = financial.waccPct != null
    ? discountedSum(flows, financial.waccPct / 100, baseIndex)
    : null;

  return {
    flowsUsd: flows,
    yearLabels,
    perPhaseAnnualRevenueUsd: perPhaseRevenue,
    blendedIrrPct: irr,
    blendedNpvUsd,
    npvBaseIndex: baseIndex,
  };
}


// Silence unused-import lint when callers only need HOURS_PER_YEAR
// transitively. Re-export so PP8 consumers can use the same constant
// without round-tripping through projectFinance.
export { HOURS_PER_YEAR };
